class ArchonNewsletterPopup {
	constructor(popup, config) {
		this.popup = popup;
		this.config = config;
		this.openButton = popup.querySelector('[data-archon-newsletter-open]');
		this.closeButtons = popup.querySelectorAll('[data-archon-newsletter-close]');
		this.form = popup.querySelector('[data-archon-newsletter-form]');
		this.emailInput = popup.querySelector('#archon-newsletter-popup-email');
		this.countrySelect = popup.querySelector('#archon-newsletter-popup-country');
		this.submitButton = popup.querySelector('.archon-newsletter-popup__submit');
		this.successCloseButton = popup.querySelector('.archon-newsletter-popup__success-close');
		this.response = popup.querySelector('[data-archon-newsletter-response]');
		this.closedCookieName = 'archon_newsletter_popup_closed';
		this.subscribedCookieName = 'archon_newsletter_subscribed';
		this.showTimer = null;
		this.isSubmitting = false;

		this.open = this.open.bind(this);
		this.close = this.close.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.updateCountryState = this.updateCountryState.bind(this);
	}

	init() {
		this.openButton.addEventListener('click', this.open);
		this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
		this.countrySelect.addEventListener('change', this.updateCountryState);
		this.form.addEventListener('submit', this.handleSubmit);
		document.addEventListener('keydown', this.handleKeydown);

		if (this.getCookie(this.subscribedCookieName)) {
			this.setCookie(this.subscribedCookieName, 400);
			return;
		}

		if (this.getCookie(this.closedCookieName)) {
			return;
		}

		this.loadCountries();
		const delay = Number.parseInt(this.config.settings.showDelaySeconds, 10) || 0;
		this.showTimer = window.setTimeout(() => this.show(), delay * 1000);
	}

	getCookie(name) {
		return document.cookie
			.split('; ')
			.find((cookie) => cookie.startsWith(`${name}=`));
	}

	setCookie(name, days) {
		const maxAge = days * 24 * 60 * 60;
		const secure = window.location.protocol === 'https:' ? '; Secure' : '';
		document.cookie = `${name}=1; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
	}

	getMessage(code) {
		return this.config.messages[code] || this.config.messages.general_error;
	}

	setState(state) {
		this.popup.dataset.state = state;
		this.openButton.setAttribute('aria-expanded', state === 'open' ? 'true' : 'false');
	}

	show() {
		this.popup.classList.add('is-visible');
		this.popup.setAttribute('aria-hidden', 'false');
	}

	hide() {
		window.clearTimeout(this.showTimer);
		this.popup.classList.remove('is-visible');
		this.popup.setAttribute('aria-hidden', 'true');
		this.setState('closed');
	}

	close() {
		const days = Number.parseInt(this.config.settings.hideAfterClosingDays, 10) || 0;

		if (days > 0) {
			this.setCookie(this.closedCookieName, days);
		}

		this.hide();
		document.activeElement?.blur();
	}

	open() {
		this.setState('open');

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.emailInput.focus();
			return;
		}

		const focusAfterOpening = (event) => {
			if (event.propertyName !== 'grid-template-rows') {
				return;
			}

			this.form.removeEventListener('transitionend', focusAfterOpening);

			if (this.popup.dataset.state === 'open') {
				this.emailInput.focus();
			}
		};

		this.form.addEventListener('transitionend', focusAfterOpening);
	}

	handleKeydown(event) {
		if (event.key === 'Escape' && this.popup.classList.contains('is-visible')) {
			this.close();
		}
	}

	showError(code, field) {
		this.response.textContent = this.getMessage(code);
		field?.focus();
	}

	updateCountryState() {
		this.countrySelect.classList.toggle('has-value', Boolean(this.countrySelect.value));
	}

	async loadCountries() {
		const requestUrl = new URL(this.config.ajaxUrl);
		requestUrl.searchParams.set('action', this.config.countries.action);
		requestUrl.searchParams.set('nonce', this.config.countries.nonce);

		try {
			const request = await fetch(requestUrl.toString(), { credentials: 'same-origin' });
			const result = await request.json();

			if (!request.ok || !result.success || !Array.isArray(result.data.countries)) {
				throw new Error('Invalid countries response');
			}

			this.countrySelect.replaceChildren(new Option(this.config.messages.select_country, ''));
			result.data.countries
				.sort((firstCountry, secondCountry) => firstCountry.name.localeCompare(secondCountry.name))
				.forEach((country) => {
					const option = document.createElement('option');
					option.value = country.code;
					option.textContent = country.name;
					this.countrySelect.append(option);
				});

			this.countrySelect.disabled = false;
			this.updateCountryState();
		} catch (error) {
			this.countrySelect.replaceChildren(new Option(this.config.messages.error_loading_countries, ''));
			this.response.textContent = this.config.messages.error_loading_countries;
		}
	}

	async handleSubmit(event) {
		event.preventDefault();

		if (this.isSubmitting) {
			return;
		}

		this.response.textContent = '';

		if (!this.emailInput.value.trim()) {
			this.showError('email_required', this.emailInput);
			return;
		}

		if (!this.emailInput.validity.valid) {
			this.showError('email_invalid', this.emailInput);
			return;
		}

		if (this.countrySelect.disabled) {
			this.showError('error_loading_countries');
			return;
		}

		if (!this.countrySelect.value) {
			this.showError('country_required', this.countrySelect);
			return;
		}

		this.isSubmitting = true;
		this.submitButton.disabled = true;
		this.submitButton.textContent = this.config.messages.subscribing;
		this.form.setAttribute('aria-busy', 'true');

		const formData = new FormData(this.form);
		formData.set('action', this.config.newsletter.action);
		formData.set('nonce', this.config.newsletter.nonce);

		try {
			const request = await fetch(this.config.ajaxUrl, {
				method: 'POST',
				body: formData,
				credentials: 'same-origin',
			});
			const result = await request.json();

			if (!request.ok || !result.success) {
				this.showError(result.code || 'subscription_failed');
				return;
			}

			this.form.reset();
			this.updateCountryState();
			this.setCookie(this.subscribedCookieName, 400);
			this.setState('success');
			this.successCloseButton.focus();
		} catch (error) {
			this.showError('general_error');
		} finally {
			this.isSubmitting = false;
			this.submitButton.disabled = false;
			this.submitButton.textContent = this.config.messages.subscribe;
			this.form.removeAttribute('aria-busy');
		}
	}
}

const popup = document.querySelector('[data-archon-newsletter-popup]');
const popupConfig = window.archonNewsletterPopup;

if (popup && popupConfig) {
	new ArchonNewsletterPopup(popup, popupConfig).init();
}
